import { useEffect, useState } from "react";
import axios from "axios";
import { BookMarked, GitBranch, GitCommitHorizontal } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * EGC (opdracht Frank 31 oktober 2026): in een werkruimte die bij een repo hoort, de vastleggingen van haar tak,
 * gelezen uit de REPO (`/api/workspaces/{id}/commits`). Indeling naar de commitlijst van GitHub: de boodschap als
 * titel, daaronder schrijver en tijd, rechts de korte sleutel. `versie` laat de lijst opnieuw lezen na Bevestig.
 */
export function wanneer(iso, taal = "nl") {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(taal === "en" ? "en-GB" : "nl-NL", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam",
  });
}

export default function RepoVastleggingen({ workspaceId, versie = 0 }) {
  const { t, taal } = useTaal();
  const [stand, setStand] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/workspaces/${workspaceId}/commits`)
      .then((r) => setStand(r.data))
      .catch(() => setStand(null));
  }, [workspaceId, versie]);

  if (!stand?.repo) return null;
  return (
    <section className="rounded-lg border" data-testid="repo-vastleggingen">
      <header className="flex flex-wrap items-center gap-2 border-b px-3 py-2 text-sm">
        <BookMarked className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold" data-testid="repo-naam-kop">{stand.repo.name}</span>
        <span className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground" data-testid="repo-tak">
          <GitBranch className="h-3 w-3" />{stand.tak}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {stand.commits.length === 1 ? t("1 vastlegging") : t("{n} vastleggingen", { n: stand.commits.length })}
        </span>
      </header>
      <ol className="divide-y">
        {stand.commits.map((c) => (
          <li key={c.sha} className="flex items-start gap-2 px-3 py-2" data-testid="vastlegging">
            <GitCommitHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block break-words text-sm">{c.boodschap}</span>
              <span className="block text-xs text-muted-foreground">{c.schrijver} · {wanneer(c.tijd, taal)}</span>
            </span>
            <code className="shrink-0 text-xs text-muted-foreground">{c.kort}</code>
          </li>
        ))}
      </ol>
    </section>
  );
}
